import { useState, useEffect, useCallback } from 'react'
import {
  ClipboardList,
  Plus,
  RefreshCw,
  ExternalLink,
  Trash2,
  CheckCircle2,
  Search,
  X,
  AlertTriangle,
  Clock,
  FileText,
  Download,
  MessageSquare,
} from 'lucide-react'
import {
  api,
  type Task,
  type TaskCategory,
  type TaskStatus,
  type TaskPriority,
  type TaskStats,
  type TaskComment,
} from '../lib/api'

// ─── Constants ───

const CATEGORIES: { value: TaskCategory; label: string; color: string; darkColor: string }[] = [
  { value: 'punch-list', label: 'Punch List', color: 'bg-amber-100 text-amber-700', darkColor: 'dark:bg-amber-900/30 dark:text-amber-400' },
  { value: 'feature', label: 'Feature', color: 'bg-blue-100 text-blue-700', darkColor: 'dark:bg-blue-900/30 dark:text-blue-400' },
  { value: 'bug', label: 'Bug', color: 'bg-red-100 text-red-700', darkColor: 'dark:bg-red-900/30 dark:text-red-400' },
  { value: 'content', label: 'Content', color: 'bg-purple-100 text-purple-700', darkColor: 'dark:bg-purple-900/30 dark:text-purple-400' },
  { value: 'infrastructure', label: 'Infra', color: 'bg-gray-100 text-gray-700', darkColor: 'dark:bg-gray-700 dark:text-gray-300' },
  { value: 'research', label: 'Research', color: 'bg-green-100 text-green-700', darkColor: 'dark:bg-green-900/30 dark:text-green-400' },
]

const STATUSES: { value: TaskStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'done', label: 'Done' },
  { value: 'deferred', label: 'Deferred' },
]

const PRIORITIES: { value: TaskPriority; label: string; color: string; darkColor: string }[] = [
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-800', darkColor: 'dark:bg-red-900/30 dark:text-red-300' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-800', darkColor: 'dark:bg-orange-900/30 dark:text-orange-300' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-100 text-amber-800', darkColor: 'dark:bg-amber-900/30 dark:text-amber-300' },
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-600', darkColor: 'dark:bg-gray-700 dark:text-gray-400' },
]

const STATUS_ICONS: Record<TaskStatus, typeof CheckCircle2> = {
  'open': Clock,
  'in-progress': RefreshCw,
  'blocked': AlertTriangle,
  'done': CheckCircle2,
  'deferred': X,
}

// ─── Component ───

export function PunchListPage() {
  const [tasks, setTasks] = useState<Task[]>([])
  const [stats, setStats] = useState<TaskStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('open')
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const [priorityFilter, setPriorityFilter] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')

  // UI state
  const [toast, setToast] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [editingTask, setEditingTask] = useState<Task | null>(null)
  const [expandedTask, setExpandedTask] = useState<string | null>(null)
  const [taskComments, setTaskComments] = useState<Record<string, TaskComment[]>>({})
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [importing, setImporting] = useState(false)

  // Create/edit form
  const [formTitle, setFormTitle] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [formCategory, setFormCategory] = useState<TaskCategory>('punch-list')
  const [formPriority, setFormPriority] = useState<TaskPriority>('medium')
  const [formStatus, setFormStatus] = useState<TaskStatus>('open')
  const [formTags, setFormTags] = useState('')

  const showToast = useCallback((msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(null), 2500)
  }, [])

  const loadTasks = useCallback(async () => {
    setLoading(true)
    try {
      const filters: Record<string, string> = { limit: '100' }
      if (statusFilter) filters.status = statusFilter
      if (categoryFilter) filters.category = categoryFilter
      if (priorityFilter) filters.priority = priorityFilter
      if (searchQuery) filters.search = searchQuery
      const result = await api.tasks.list(filters)
      setTasks(result.tasks)
      setTotal(result.total)
    } catch (err) {
      console.error('Failed to load tasks:', err)
    } finally {
      setLoading(false)
    }
  }, [statusFilter, categoryFilter, priorityFilter, searchQuery])

  const loadStats = useCallback(async () => {
    try {
      const s = await api.tasks.stats()
      setStats(s)
    } catch (err) {
      console.error('Failed to load stats:', err)
    }
  }, [])

  useEffect(() => { loadTasks() }, [loadTasks])
  useEffect(() => { loadStats() }, [loadStats])

  const openCreateForm = () => {
    setEditingTask(null)
    setFormTitle('')
    setFormDescription('')
    setFormCategory('punch-list')
    setFormPriority('medium')
    setFormStatus('open')
    setFormTags('')
    setShowCreate(true)
  }

  const openEditForm = (task: Task) => {
    setEditingTask(task)
    setFormTitle(task.title)
    setFormDescription(task.description || '')
    setFormCategory(task.category)
    setFormPriority(task.priority)
    setFormStatus(task.status)
    setFormTags(task.tags.join(', '))
    setShowCreate(true)
  }

  const saveTask = async () => {
    if (!formTitle.trim()) return
    const body: Partial<Task> = {
      title: formTitle.trim(),
      description: formDescription.trim() || null,
      category: formCategory,
      priority: formPriority,
      status: formStatus,
      tags: formTags.split(',').map((t) => t.trim()).filter(Boolean),
    }
    try {
      if (editingTask) {
        await api.tasks.patch(editingTask.id, body)
        showToast('Task updated')
      } else {
        await api.tasks.create(body)
        showToast('Task created')
      }
      setShowCreate(false)
      loadTasks()
      loadStats()
    } catch {
      showToast('Save failed')
    }
  }

  const deleteTask = async (id: string) => {
    try {
      await api.tasks.delete(id)
      showToast('Task deleted')
      loadTasks()
      loadStats()
    } catch {
      showToast('Delete failed')
    }
  }

  const toggleStatus = async (task: Task, newStatus: TaskStatus) => {
    try {
      await api.tasks.patch(task.id, { status: newStatus })
      loadTasks()
      loadStats()
    } catch {
      showToast('Status update failed')
    }
  }

  const loadComments = async (taskId: string) => {
    try {
      const comments = await api.tasks.getComments(taskId)
      setTaskComments((prev) => ({ ...prev, [taskId]: comments }))
    } catch {
      // ignore
    }
  }

  const toggleExpanded = (taskId: string) => {
    if (expandedTask === taskId) {
      setExpandedTask(null)
    } else {
      setExpandedTask(taskId)
      if (!taskComments[taskId]) loadComments(taskId)
    }
  }

  const importBriefings = async () => {
    setImporting(true)
    try {
      const result = await api.tasks.importAll()
      showToast(`Imported ${result.totalImported}, skipped ${result.totalSkipped}`)
      loadTasks()
      loadStats()
    } catch {
      showToast('Import failed')
    } finally {
      setImporting(false)
    }
  }

  const bulkDone = async () => {
    if (selectedIds.size === 0) return
    try {
      await api.tasks.bulkStatus(Array.from(selectedIds), 'done')
      showToast(`${selectedIds.size} tasks marked done`)
      setSelectedIds(new Set())
      loadTasks()
      loadStats()
    } catch {
      showToast('Bulk update failed')
    }
  }

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const getCatMeta = (cat: TaskCategory) => CATEGORIES.find((c) => c.value === cat)
  const getPriorityMeta = (pri: TaskPriority) => PRIORITIES.find((p) => p.value === pri)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <ClipboardList className="w-6 h-6 text-amber-500" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Task Board</h1>
          {stats && (
            <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 rounded-full">
              {stats.total}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <button onClick={bulkDone} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors">
              <CheckCircle2 className="w-4 h-4" />
              Done ({selectedIds.size})
            </button>
          )}
          <button
            onClick={importBriefings}
            disabled={importing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-surface-800 border border-gray-200 dark:border-surface-700 rounded-lg hover:bg-gray-50 dark:hover:bg-surface-700 transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            {importing ? 'Importing...' : 'Import Briefings'}
          </button>
          <button onClick={openCreateForm} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors">
            <Plus className="w-4 h-4" />
            New Task
          </button>
          <button onClick={() => { loadTasks(); loadStats() }} className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {STATUSES.map((s) => (
            <button
              key={s.value}
              onClick={() => setStatusFilter(statusFilter === s.value ? '' : s.value)}
              className={`px-3 py-2 rounded-lg text-center transition-colors border ${
                statusFilter === s.value
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                  : 'border-gray-200 dark:border-surface-700 bg-white dark:bg-surface-800 text-gray-600 dark:text-gray-400'
              }`}
            >
              <div className="text-lg font-bold">{stats.byStatus[s.value] || 0}</div>
              <div className="text-[10px] uppercase font-medium">{s.label}</div>
            </button>
          ))}
          {stats.overdue > 0 && (
            <div className="px-3 py-2 rounded-lg text-center border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">
              <div className="text-lg font-bold">{stats.overdue}</div>
              <div className="text-[10px] uppercase font-medium">Overdue</div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search tasks..."
            className="w-full pl-8 pr-3 py-1.5 text-sm bg-white dark:bg-surface-800 border border-gray-200 dark:border-surface-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white placeholder-gray-400"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setCategoryFilter(categoryFilter === cat.value ? '' : cat.value)}
              className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                categoryFilter === cat.value
                  ? `${cat.color} ${cat.darkColor} ring-1 ring-current`
                  : 'bg-gray-100 text-gray-500 dark:bg-surface-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-surface-600'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          className="px-2.5 py-1.5 text-xs font-medium bg-white dark:bg-surface-800 border border-gray-200 dark:border-surface-700 rounded-lg text-gray-700 dark:text-gray-300"
        >
          <option value="">All Priorities</option>
          {PRIORITIES.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      </div>

      {/* Task List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-16">
          <ClipboardList className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-500 dark:text-gray-400">No tasks match your filters.</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            Create a task or import from briefings.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-xs text-gray-400 dark:text-gray-500 px-1">
            Showing {tasks.length} of {total}
          </div>
          {tasks.map((task) => {
            const catMeta = getCatMeta(task.category)
            const priMeta = getPriorityMeta(task.priority)
            const StatusIcon = STATUS_ICONS[task.status] || Clock
            const isExpanded = expandedTask === task.id
            const isSelected = selectedIds.has(task.id)

            return (
              <div
                key={task.id}
                className={`bg-white dark:bg-surface-800 border rounded-xl transition-colors group ${
                  isSelected
                    ? 'border-blue-400 dark:border-blue-600'
                    : 'border-gray-200 dark:border-surface-700 hover:border-gray-300 dark:hover:border-surface-600'
                }`}
              >
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(task.id)}
                      className="mt-1 w-4 h-4 rounded border-gray-300 dark:border-surface-600 text-blue-600 focus:ring-blue-500"
                    />
                    <button
                      onClick={() => toggleStatus(task, task.status === 'done' ? 'open' : 'done')}
                      className={`mt-0.5 p-1 rounded-md transition-colors ${
                        task.status === 'done'
                          ? 'text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20'
                          : task.status === 'blocked'
                          ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
                          : task.status === 'in-progress'
                          ? 'text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                          : 'text-gray-400 hover:bg-gray-50 dark:hover:bg-surface-700'
                      }`}
                      title={`Status: ${task.status}`}
                    >
                      <StatusIcon className="w-4 h-4" />
                    </button>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {priMeta && (
                          <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase rounded ${priMeta.color} ${priMeta.darkColor}`}>
                            {priMeta.label}
                          </span>
                        )}
                        {catMeta && (
                          <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase rounded ${catMeta.color} ${catMeta.darkColor}`}>
                            {catMeta.label}
                          </span>
                        )}
                        {task.source_type === 'briefing' && task.source_ref && (
                          <span className="text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-0.5">
                            <FileText className="w-3 h-3" />
                            {task.source_ref}
                          </span>
                        )}
                      </div>

                      <h3
                        className={`text-sm font-semibold leading-snug cursor-pointer ${
                          task.status === 'done'
                            ? 'text-gray-400 dark:text-gray-500 line-through'
                            : 'text-gray-900 dark:text-white'
                        }`}
                        onClick={() => openEditForm(task)}
                      >
                        {task.title}
                      </h3>

                      {task.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2 leading-relaxed">
                          {task.description}
                        </p>
                      )}

                      <div className="flex items-center flex-wrap gap-2 mt-2">
                        {task.linked_document_ids.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {task.linked_document_ids.map((docId) => (
                              <span
                                key={docId}
                                className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded"
                              >
                                {docId}
                              </span>
                            ))}
                          </div>
                        )}
                        {task.tags.map((tag) => (
                          <span key={tag} className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-600 dark:bg-surface-700 dark:text-gray-400 rounded">
                            {tag}
                          </span>
                        ))}
                        {task.source_url && (
                          <a
                            href={task.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-0.5 text-[10px] text-blue-500 hover:text-blue-600 dark:text-blue-400"
                          >
                            <ExternalLink className="w-3 h-3" />
                            {task.source_name || 'Source'}
                          </a>
                        )}
                        {task.due_date && (
                          <span className={`text-[10px] ${
                            new Date(task.due_date) < new Date() && task.status !== 'done'
                              ? 'text-red-500 font-bold'
                              : 'text-gray-400 dark:text-gray-500'
                          }`}>
                            Due: {new Date(task.due_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => toggleExpanded(task.id)}
                        className="p-1.5 text-gray-400 hover:text-blue-500 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/10"
                        title="Comments"
                      >
                        <MessageSquare className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteTask(task.id)}
                        className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/10"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-4 pb-4 pt-0 border-t border-gray-100 dark:border-surface-700">
                    <div className="mt-3 space-y-2">
                      {(taskComments[task.id] || []).map((c) => (
                        <div key={c.id} className="flex gap-2 text-xs">
                          <span className={`font-medium ${c.author === 'claude' ? 'text-purple-500' : c.author === 'system' ? 'text-gray-400' : 'text-blue-500'}`}>
                            {c.author}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 flex-1">{c.content}</span>
                          <span className="text-gray-400 dark:text-gray-500 whitespace-nowrap">{new Date(c.created_at).toLocaleDateString()}</span>
                        </div>
                      ))}
                      {(taskComments[task.id] || []).length === 0 && (
                        <p className="text-xs text-gray-400 dark:text-gray-500">No comments yet.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowCreate(false)}>
          <div className="bg-white dark:bg-surface-800 rounded-2xl shadow-xl border border-gray-200 dark:border-surface-700 w-full max-w-lg mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              {editingTask ? 'Edit Task' : 'New Task'}
            </h2>
            <div className="space-y-3">
              <input
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="Task title"
                className="w-full px-3 py-2 text-sm bg-white dark:bg-surface-900 border border-gray-200 dark:border-surface-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
                autoFocus
              />
              <textarea
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Description (optional)"
                rows={3}
                className="w-full px-3 py-2 text-sm bg-white dark:bg-surface-900 border border-gray-200 dark:border-surface-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white resize-none"
              />
              <div className="grid grid-cols-3 gap-2">
                <select
                  value={formCategory}
                  onChange={(e) => setFormCategory(e.target.value as TaskCategory)}
                  className="px-2.5 py-2 text-sm bg-white dark:bg-surface-900 border border-gray-200 dark:border-surface-700 rounded-lg text-gray-700 dark:text-gray-300"
                >
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <select
                  value={formPriority}
                  onChange={(e) => setFormPriority(e.target.value as TaskPriority)}
                  className="px-2.5 py-2 text-sm bg-white dark:bg-surface-900 border border-gray-200 dark:border-surface-700 rounded-lg text-gray-700 dark:text-gray-300"
                >
                  {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                <select
                  value={formStatus}
                  onChange={(e) => setFormStatus(e.target.value as TaskStatus)}
                  className="px-2.5 py-2 text-sm bg-white dark:bg-surface-900 border border-gray-200 dark:border-surface-700 rounded-lg text-gray-700 dark:text-gray-300"
                >
                  {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <input
                type="text"
                value={formTags}
                onChange={(e) => setFormTags(e.target.value)}
                placeholder="Tags (comma separated)"
                className="w-full px-3 py-2 text-sm bg-white dark:bg-surface-900 border border-gray-200 dark:border-surface-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
              />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-surface-700 rounded-lg hover:bg-gray-200 dark:hover:bg-surface-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveTask}
                disabled={!formTitle.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {editingTask ? 'Save' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 bg-gray-900 dark:bg-surface-700 text-white text-sm font-medium rounded-lg shadow-lg border border-gray-700 dark:border-surface-600">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-400" />
            {toast}
          </div>
        </div>
      )}
    </div>
  )
}
